import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import {
    alpha,
    Box,
    Typography, useMediaQuery, Tooltip,
    ThemeProvider, Link, Skeleton, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useTheme } from '@mui/material';
import { reportColors, styleReportChart, createReportSurfaceTheme, getReportSurfaceSx, getReportPageSx } from '../shared/report-design';


import { AppVarsContext } from '../../context/AppVarsContext';
import ReactApexChart from 'react-apexcharts';
import { buildApexTooltip } from '../../system/apex-tooltip';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';
import { resolveShowCost } from '../../system/report-visibility';
import { formatUsd } from '../../system/report-formatters';
import { normalizePlatformName } from './platform-data';
import ReportPerformanceOverview from '../shared/ReportPerformanceOverview';


const Overview = (props) => {
    const [campaignGP, setcampaignGP] = useState(null);
    const [overview, setOverview] = useState([]);
    const [allOverview, setallOverview] = useState({});
    const [loading, setLoading] = useState(true);
    const adsets = useMemo(() => {
        const raw = props.reportProfile?.settings;
        if (!raw) return null;
        try {
            return typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (err) {
            console.error('Invalid reportProfile.settings JSON:', err);
            return null;
        }
    }, [props.reportProfile?.settings]);
    const { appVars, appLoading } = useContext(AppVarsContext);


    const showCost = resolveShowCost(props.reportProfile);
    
    //console.log(`admin token`,admintoken);
//console.log(`props`,props);
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const surfaceCardSx = getReportSurfaceSx(theme);
    const metricTileSx = { ...surfaceCardSx, p: 2 };
    const pageSx = { ...getReportPageSx(theme), '& .MuiLink-root': { color: reportColors.revenue, display: 'inline-flex', alignItems: 'center', minHeight: 44 } };
    const sectionHeaderSx = {
        px: 2.5,
        py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
    };
    const summaryHeaderCellSx = {
        fontWeight: 700,
        fontSize: 12,
        color: theme.palette.text.secondary,
        bgcolor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`
    };
    const chartPalette = useMemo(() => ({
        primary: { colors: [reportColors.revenue] },
        accent: { colors: [reportColors.conversions] },
        dual: { colors: [reportColors.revenue, reportColors.conversions] },
        donut: { colors: reportColors.categories }
    }), []);

    const sumFields = (data) => {
        const rows = Array.isArray(data) ? data : [];
        return rows.reduce((acc, obj) => {
            const keys = ['IMPRESSIONS', 'CLICKS', 'CONVERSIONS', 'REVENUE', 'COST'];
            keys.forEach(key => {
                acc[key] = (acc[key] || 0) + (Number(obj?.[key]) || 0);
            });
            return acc;
        }, {});
    };

    const formatCompact = (value) =>
        new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value || 0));

    const formatNumber = (value) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));

    const formatMoney = (value) => formatUsd(value);

    const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

    const normalizeConversionSpec = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value
                .map((entry) => {
                    if (entry == null) return null;
                    if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
                    if (typeof entry === 'object') {
                        return entry.id || entry.action_type || entry.resourceName || entry.conversion || entry.name || entry.value || null;
                    }
                    return null;
                })
                .filter(Boolean);
        }
        if (typeof value === 'object') {
            const nested =
                value?.conversions ||
                value?.conversion ||
                value?.conversion_actions ||
                value?.conversionActions ||
                value?.values ||
                [];
            return normalizeConversionSpec(nested);
        }
        return [];
    };

    const hasConversionSpecValue = (value) => {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        if (typeof value === 'string') return value.trim().length > 0;
        if (typeof value === 'number') return Number.isFinite(value);
        return false;
    };

    const getGoogleSpecListForService = (spec, adSetKey) => {
        if (!spec) return [];
        if (Array.isArray(spec)) return normalizeConversionSpec(spec);
        if (typeof spec === 'object') {
            const hasPaidGrant = spec.paid !== undefined || spec.grant !== undefined;
            if (hasPaidGrant) {
                const key = adSetKey === 'google_grant' ? 'grant' : 'paid';
                return normalizeConversionSpec(spec[key] || []);
            }
            return normalizeConversionSpec(spec);
        }
        return [];
    };

    const pickGoogleConversionSpec = (settings, adSetKey) => {
        if (!settings || typeof settings !== 'object') return null;
        const candidates = [
            settings?.conversion_defaults?.[adSetKey],
            settings?.conversion_defaults?.google_paid,
            settings?.conversion_defaults?.google_grant,
            settings?.conversion_defaults?.google,
            settings?.conversion_defaults?.adwords,
            settings?.conversionActions?.google,
            settings?.conversion_actions?.google,
            settings?.conversionSpec?.google,
            settings?.conversion_spec?.google,
            settings?.conversions?.google,
            settings?.conversionActions?.adwords,
            settings?.conversion_actions?.adwords,
            settings?.conversionSpec?.adwords,
            settings?.conversion_spec?.adwords,
            settings?.conversions?.adwords,
            settings?.conversionActions,
            settings?.conversion_actions,
            settings?.conversionSpec,
            settings?.conversion_spec,
            settings?.conversions,
            settings?.ad_sets?.[adSetKey]?.conversionActions,
            settings?.ad_sets?.[adSetKey]?.conversion_actions,
            settings?.ad_sets?.[adSetKey]?.conversionSpec,
            settings?.ad_sets?.[adSetKey]?.conversion_spec,
            settings?.ad_sets?.[adSetKey]?.conversions
        ];
        return candidates.find(hasConversionSpecValue) || null;
    };

    const getDefaultConversions = (settings, platform) => {
        if (!settings || typeof settings !== 'object') return [];
        try {
            if (platform === 'meta') {
                const candidates = [
                    settings?.conversion_defaults?.meta,
                    settings?.conversionActions?.meta,
                    settings?.conversion_actions?.meta,
                    settings?.conversionSpec?.meta,
                    settings?.conversion_spec?.meta,
                    settings?.conversions?.meta,
                    settings?.conversionActions,
                    settings?.conversion_actions,
                    settings?.conversionSpec,
                    settings?.conversion_spec,
                    settings?.conversions,
                    settings?.ad_sets?.meta?.conversionActions,
                    settings?.ad_sets?.meta?.conversion_actions,
                    settings?.ad_sets?.meta?.conversionSpec,
                    settings?.ad_sets?.meta?.conversion_spec,
                    settings?.ad_sets?.meta?.conversions
                ];
                const found = candidates.find((item) => Array.isArray(item) && item.length > 0) || [];
                return normalizeConversionSpec(found);
            }
            if (platform === 'google') {
                const paidRaw = pickGoogleConversionSpec(settings, 'google_paid');
                const grantRaw = pickGoogleConversionSpec(settings, 'google_grant');
                return {
                    paid: getGoogleSpecListForService(paidRaw, 'google_paid'),
                    grant: getGoogleSpecListForService(grantRaw, 'google_grant')
                };
            }
        } catch (error) {
            return [];
        }
        return [];
    };

    const fetchData = async () => {
        setcampaignGP(null);
        setLoading(true);
        if (typeof props.setLoading === 'function') props.setLoading(true);

        try {
            if (!appVars?.api_url) {
                throw new Error('Missing appVars.api_url');
            }
            if (!adsets?.ad_sets) {
                throw new Error('Missing ad_sets in reportProfile.settings');
            }

            const metaConversionSpec = getDefaultConversions(adsets, 'meta');
            const googleConversionSpec = getDefaultConversions(adsets, 'google');
            console.log('Meta conversion spec:', googleConversionSpec);
            const raw = JSON.stringify({
                cid: sessionStorage.getItem('rid'),
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate'),
                account: "Paid",
              
                gmcid: props.reportProfile?.gmcid,
                metaConversionSpec,
                googleConversionSpec
            });

            const requestOptions = {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: raw,
                redirect: 'follow'
            };

            const res = await fetch(`${appVars.api_url}/platform-overview`, { ...requestOptions, credentials: 'include' });
            if (!res.ok) {
                throw new Error(`platform-overview ${res.status}`);
            }
            const response = await res.json();
            const cleaned = Array.isArray(response)
                ? response
                : (response?.rows || response?.data || []);

            setcampaignGP(cleaned);
            setallOverview(sumFields(cleaned));
            setOverview(cleaned);
        } catch (err) {
            console.error('fetch error:', err);
            setcampaignGP([]);
            setOverview([]);
            setallOverview({});
        } finally {
            setLoading(false);
            if (typeof props.setLoading === 'function') props.setLoading(false);
        }
    };

    const hasFetched = useRef(false);

    useEffect(() => {
        if (appLoading || !adsets?.ad_sets) return;
        if (!hasFetched.current) {
            fetchData();
            hasFetched.current = true;
        }
    }, [appLoading, adsets]);



    useEffect(() => {
        const handleStorageChange = () => {
            if (!appLoading && adsets?.ad_sets) {
                fetchData();
            }
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [appLoading, adsets]);

    const platformSummary = useMemo(() => {
        const rows = Array.isArray(overview) ? overview : [];
        return rows.map((row) => {
            const impressions = Number(row?.IMPRESSIONS || 0);
            const clicks = Number(row?.CLICKS || 0);
            const conversions = Number(row?.CONVERSIONS || 0);
            const revenue = Number(row?.REVENUE ?? row?.revenue ?? 0);
            const cost = Number(row?.COST || 0);
            return {
                name: normalizePlatformName(row?.name || row?.Platform || row?.platform || 'Platform'),
                url: row?.url,
                revenueAvailable: row?.revenueAvailable !== false,
                error: row?.error || null,
                impressions,
                clicks,
                conversions,
                revenue,
                cost,
                avgValue: conversions ? (revenue / conversions) : 0,
                ctr: impressions ? (clicks / impressions) * 100 : 0,
                convRate: clicks ? (conversions / clicks) * 100 : 0,
                roas: cost ? (revenue / cost) : 0
            };
        });
    }, [overview]);

    const totals = useMemo(() => {
        const impressions = Number(allOverview?.IMPRESSIONS || 0);
        const clicks = Number(allOverview?.CLICKS || 0);
        const conversions = Number(allOverview?.CONVERSIONS || 0);
        const revenue = Number(allOverview?.REVENUE || 0);
        const cost = Number(allOverview?.COST || 0);
        const activePlatforms = platformSummary.filter((row) => (
            row.impressions > 0 ||
            row.clicks > 0 ||
            row.conversions > 0 ||
            row.revenue > 0 ||
            row.cost > 0
        )).length;
        return {
            impressions,
            clicks,
            conversions,
            revenue,
            cost,
            activePlatforms,
            ctr: impressions ? (clicks / impressions) * 100 : 0,
            convRate: clicks ? (conversions / clicks) * 100 : 0,
            roas: cost ? (revenue / cost) : 0,
            avgValue: conversions ? (revenue / conversions) : 0,
            cpc: clicks ? (cost / clicks) : 0
        };
    }, [allOverview, platformSummary]);

    const topRevenuePlatform = useMemo(() => (
        [...platformSummary].sort((a, b) => b.revenue - a.revenue)[0] || null
    ), [platformSummary]);

    const topConversionPlatform = useMemo(() => (
        [...platformSummary].sort((a, b) => b.conversions - a.conversions)[0] || null
    ), [platformSummary]);

    const formatMetricByKey = (key, value) => {
        if (key === 'revenue' || key === 'cost') return formatMoney(value);
        if (key === 'ctr' || key === 'convRate') return formatPercent(value);
        if (key === 'roas') return `${Number(value || 0).toFixed(2)}x`;
        return formatNumber(value);
    };

    const platformMetric = useMemo(() => {
        if (!platformSummary.length) return { key: 'conversions', label: 'Conversions', rows: [] };
        const hasRevenue = platformSummary.some((row) => row.revenue > 0);
        let key = hasRevenue ? 'revenue' : 'conversions';
        let label = hasRevenue ? 'Revenue' : 'Conversions';

        const total = platformSummary.reduce((sum, row) => sum + Number(row[key] || 0), 0);
        if (total === 0) {
            key = 'clicks';
            label = 'Clicks';
        }
        if (platformSummary.reduce((sum, row) => sum + Number(row[key] || 0), 0) === 0) {
            key = 'impressions';
            label = 'Impressions';
        }

        const rows = [...platformSummary].sort((a, b) => (Number(b[key] || 0) - Number(a[key] || 0)));
        return { key, label, rows };
    }, [platformSummary]);

    const shareMetric = useMemo(() => {
        if (!platformSummary.length) return { key: 'conversions', label: 'Conversions', total: 0 };
        let key = 'revenue';
        let label = 'Revenue';
        let total = platformSummary.reduce((sum, row) => sum + Number(row.revenue || 0), 0);
        if (total === 0) {
            key = 'conversions';
            label = 'Conversions';
            total = platformSummary.reduce((sum, row) => sum + Number(row.conversions || 0), 0);
        }
        if (total === 0) {
            key = 'clicks';
            label = 'Clicks';
            total = platformSummary.reduce((sum, row) => sum + Number(row.clicks || 0), 0);
        }
        if (total === 0) {
            key = 'impressions';
            label = 'Impressions';
            total = platformSummary.reduce((sum, row) => sum + Number(row.impressions || 0), 0);
        }
        return { key, label, total };
    }, [platformSummary]);

    const shareSeries = useMemo(() => {
        if (!platformSummary.length) return [];
        return platformSummary.map((row) => Number(row[shareMetric.key] || 0));
    }, [platformSummary, shareMetric.key]);

    const shareLabels = useMemo(() => {
        if (!platformSummary.length) return [];
        return platformSummary.map((row) => row.name);
    }, [platformSummary]);

    const shareBreakdown = useMemo(() => {
        if (!platformSummary.length || !shareMetric.total) return [];
        return [...platformSummary]
            .map((row) => ({
                name: row.name,
                value: Number(row[shareMetric.key] || 0),
                percent: shareMetric.total ? (Number(row[shareMetric.key] || 0) / shareMetric.total) * 100 : 0
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 4);
    }, [platformSummary, shareMetric]);

    const topPlatformChart = useMemo(() => {
        if (!platformMetric.rows.length) return null;
        const topRows = platformMetric.rows.slice(0, 6);
        return {
            series: [
                {
                    name: platformMetric.label,
                    data: topRows.map((row) => Number(row[platformMetric.key] || 0))
                }
            ],
            options: {
                chart: { type: 'bar', toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                colors: chartPalette.primary.colors,

                xaxis: {
                    categories: topRows.map((row) => row.name),
                    labels: {
                        formatter: (val) => (platformMetric.key === 'revenue' ? `$${formatCompact(val)}` : formatCompact(val))
                    }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => formatMetricByKey(platformMetric.key, val)
                })
            }
        };
    }, [chartPalette.primary.colors, formatMetricByKey, platformMetric, theme]);

    const shareChart = useMemo(() => {
        if (!shareSeries.length) return null;
        return {
            series: shareSeries,
            options: {
                labels: shareLabels,
                chart: { type: 'donut' },
                plotOptions: { pie: { donut: { size: '72%' } } },
                legend: { position: 'bottom' },
                dataLabels: { enabled: false },
                colors: chartPalette.donut.colors,

                tooltip: buildApexTooltip(theme, {
                    shared: false,
                    valueFormatter: (val) => formatMetricByKey(shareMetric.key, val)
                })
            }
        };
    }, [chartPalette.donut.colors, formatMetricByKey, shareMetric.key, shareSeries, shareLabels, theme]);

    const conversionMixChart = useMemo(() => {
        if (!platformSummary.length) return null;
        const hasData = platformSummary.some((row) => row.clicks > 0 || row.conversions > 0);
        if (!hasData) return null;
        return {
            series: [
                { name: 'Clicks', data: platformSummary.map((row) => row.clicks) },
                { name: 'Conversions', data: platformSummary.map((row) => row.conversions) }
            ],
            options: {
                chart: { type: 'bar', toolbar: { show: false } },
                plotOptions: { bar: { columnWidth: '52%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                colors: chartPalette.dual.colors,

                xaxis: {
                    categories: platformSummary.map((row) => row.name),
                    labels: { rotate: -15 }
                },
                yaxis: {
                    labels: { formatter: (val) => formatCompact(val) }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => formatNumber(val)
                })
            }
        };
    }, [chartPalette.dual.colors, platformSummary, theme]);

    const efficiencyChart = useMemo(() => {
        if (!platformSummary.length) return null;
        return {
            series: [
                { name: 'CTR', data: platformSummary.map((row) => row.ctr) },
                { name: 'Conv Rate', data: platformSummary.map((row) => row.convRate) }
            ],
            options: {
                chart: { type: 'bar', toolbar: { show: false } },
                plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                colors: chartPalette.dual.colors,

                xaxis: {
                    categories: platformSummary.map((row) => row.name),
                    labels: { rotate: -15 }
                },
                yaxis: {
                    labels: { formatter: (val) => `${Number(val).toFixed(1)}%` }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => `${Number(val).toFixed(2)}%`
                })
            }
        };
    }, [chartPalette.dual.colors, platformSummary, theme]);

    const roasChart = useMemo(() => {
        if (!showCost || !platformSummary.length) return null;
        const hasData = platformSummary.some((row) => row.roas > 0);
        if (!hasData) return null;
        return {
            series: [{ name: 'ROAS', data: platformSummary.map((row) => row.roas) }],
            options: {
                chart: { type: 'bar', toolbar: { show: false } },
                plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 4 } },
                dataLabels: { enabled: false },
                colors: chartPalette.accent.colors,

                xaxis: {
                    categories: platformSummary.map((row) => row.name),
                    labels: { formatter: (val) => `${Number(val).toFixed(1)}x` }
                },
                tooltip: buildApexTooltip(theme, {
                    valueFormatter: (val) => `${Number(val).toFixed(2)}x`
                })
            }
        };
    }, [chartPalette.accent.colors, platformSummary, showCost, theme]);

    const platformEfficiencyCard = useMemo(() => {
        if (showCost && roasChart) {
            return {
                title: 'Return Efficiency By Platform',
                subtitle: 'ROAS by platform',
                chart: roasChart,
                empty: 'No return data available yet.'
            };
        }
        return {
            title: 'Conversion Efficiency By Platform',
            subtitle: 'CTR vs conversion rate',
            chart: efficiencyChart,
            empty: 'No efficiency metrics available yet.'
        };
    }, [efficiencyChart, roasChart, showCost]);

    if (loading) {
        const statCount = 4;
        return (
            <Box
                sx={{ ...pageSx, p: 2 }}
            >
                <Card variant="outlined" sx={{ ...surfaceCardSx, mb: 2 }}>
                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.04em' }}>
                            Preparing your performance overview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Loading revenue, conversion, and return insight for the active date range.
                        </Typography>
                    </CardContent>
                </Card>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Array.from({ length: statCount }).map((_, index) => (
                        <Skeleton
                            key={`overview-stat-${index}`}
                            variant="rectangular"
                            height={90}
                            sx={{
                                ...metricTileSx,
                                flex: '1 1 160px',
                                position: 'relative'
                            }}
                        />
                    ))}
                </Box>
                <Box sx={{ mt: 2, position: 'relative' }}>
                    <Skeleton variant="rectangular" height={280} sx={surfaceCardSx} />
                </Box>
            </Box>
        );
    }



    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={pageSx}
            >

                {campaignGP ? (
                    <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                        <ReportPerformanceOverview
                            eyebrow="Cross-channel overview" title="From reach to results"
                            description={`See which channels attract attention, generate response, and contribute attributed outcomes during ${sessionStorage.getItem('sDate')} – ${sessionStorage.getItem('eDate')}.`}
                            badge={`${totals.activePlatforms} active platforms`}
                            stages={[
                                { label: 'Acquisition', description: 'How much visibility and traffic the channels earned', metrics: [
                                    { label: 'Impressions', value: formatCompact(totals.impressions), detail: 'Ads served across platforms' },
                                    { label: 'Clicks', value: formatCompact(totals.clicks), detail: 'Traffic generated' }
                                ] },
                                { label: 'Response', description: 'How effectively exposure became action', metrics: [
                                    { label: 'CTR', value: formatPercent(totals.ctr), detail: 'Clicks relative to impressions' },
                                    { label: 'Conversion rate', value: formatPercent(totals.convRate), detail: 'Conversions relative to clicks' }
                                ] },
                                { label: 'Outcome', description: 'Attributed actions and their value', metrics: [
                                    { label: 'Conversions', value: formatCompact(totals.conversions), detail: 'Recorded conversions' },
                                    { label: 'Revenue', value: formatMoney(totals.revenue), detail: 'Attributed revenue' }
                                ] }
                            ]}
                            supportMetrics={[{ label: 'Average value', value: formatMoney(totals.avgValue) }, ...(showCost ? [{ label: 'Spend', value: formatMoney(totals.cost) }, { label: 'ROAS', value: `${totals.roas.toFixed(2)}x` }, { label: 'CPC', value: formatMoney(totals.cpc) }] : [])]}
                            insight={topRevenuePlatform?.revenue > 0 ? `${topRevenuePlatform.name} leads attributed revenue with ${formatMoney(topRevenuePlatform.revenue)}. Compare its traffic and conversion efficiency with the other channels below.` : `${formatNumber(totals.clicks)} clicks produced ${formatNumber(totals.conversions)} recorded conversions across the active platforms.`}
                            highlights={[
                                { label: 'Revenue leader', title: topRevenuePlatform?.revenue > 0 ? topRevenuePlatform.name : 'No revenue recorded', value: formatMoney(topRevenuePlatform?.revenue), detail: 'Largest contribution to attributed revenue.' },
                                { label: 'Conversion leader', title: topConversionPlatform?.conversions > 0 ? topConversionPlatform.name : 'No conversions recorded', value: formatNumber(topConversionPlatform?.conversions), detail: 'Most recorded actions in this range.' },
                                { label: 'Leading channel', title: platformMetric.rows[0]?.name || 'No platform data', value: formatMetricByKey(platformMetric.key, platformMetric.rows[0]?.[platformMetric.key]), detail: `Ranked by ${platformMetric.label.toLowerCase()} for the selected range.` }
                            ]}
                        />
                        <Box sx={{ display: 'grid', gap: 2 }}>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: 'repeat(2, minmax(0, 1fr))'
                                    }
                                }}
                            >
                                <Card variant="outlined" sx={surfaceCardSx}>
                                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Platform Performance
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Ranked by {platformMetric.label}
                                            </Typography>
                                        </Box>
                                        {topPlatformChart ? (
                                            <ReactApexChart
                                                options={styleReportChart(theme, topPlatformChart.options)}
                                                series={topPlatformChart.series}
                                                type="bar"
                                                height={280}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No platform ranking available yet.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined" sx={surfaceCardSx}>
                                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                                        <Box>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Platform Share
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Share of {shareMetric.label}
                                            </Typography>
                                        </Box>
                                        {shareChart ? (
                                            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, alignItems: 'center' }}>
                                                <ReactApexChart
                                                    options={styleReportChart(theme, shareChart.options)}
                                                    series={shareChart.series}
                                                    type="donut"
                                                    height={isDesktop ? 260 : 240}
                                                />
                                                <Box sx={{ display: 'grid', gap: 1.25 }}>
                                                    {shareBreakdown.map((row, index) => (
                                                        <Box key={`${row.name}-${index}`} sx={{ display: 'grid', gap: 0.45 }}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                                <Typography variant="caption" noWrap title={row.name}>
                                                                    {row.name}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                                    {row.percent.toFixed(1)}%
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ height: 6, borderRadius: 999, background: theme.palette.divider }}>
                                                                <Box
                                                                    sx={{
                                                                        height: '100%',
                                                                        width: `${row.percent}%`,
                                                                        borderRadius: 999,
                                                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {shareMetric.label}: {formatMetricByKey(shareMetric.key, row.value)}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No platform distribution available yet.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(2, minmax(0, 1fr))'
                                    }
                                }}
                            >
                                <Card variant="outlined" sx={surfaceCardSx}>
                                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Conversion Mix By Platform
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Clicks vs conversions
                                            </Typography>
                                        </Box>
                                        {conversionMixChart ? (
                                            <ReactApexChart
                                                options={styleReportChart(theme, conversionMixChart.options)}
                                                series={conversionMixChart.series}
                                                type="bar"
                                                height={280}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No conversion mix available yet.
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card variant="outlined" sx={surfaceCardSx}>
                                    <CardContent sx={{ display: 'grid', gap: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {platformEfficiencyCard.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {platformEfficiencyCard.subtitle}
                                            </Typography>
                                        </Box>
                                        {platformEfficiencyCard.chart ? (
                                            <ReactApexChart
                                                options={styleReportChart(theme, platformEfficiencyCard.chart.options)}
                                                series={platformEfficiencyCard.chart.series}
                                                type="bar"
                                                height={280}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {platformEfficiencyCard.empty}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>

                            <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                                {isDesktop ? (
                                    <Card variant="outlined" sx={surfaceCardSx}>
                                        <CardContent sx={{ p: 0 }}>
                                            <Box sx={sectionHeaderSx}>
                                                <Typography component="h2" variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                    Platform Summary
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Revenue, conversion, and delivery performance by platform
                                                </Typography>
                                            </Box>
                                            <TableContainer sx={{ px: 1.25, pb: 1.25 }}>
                                                <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={summaryHeaderCellSx}>Platform</TableCell>
                                                                <TableCell align="right" sx={summaryHeaderCellSx}>Impressions</TableCell>
                                                                <TableCell data-report-metric="click" align="right" sx={summaryHeaderCellSx}>Clicks</TableCell>
                                                                <TableCell data-report-metric="click" align="right" sx={summaryHeaderCellSx}>CTR</TableCell>
                                                                <TableCell data-report-metric="conversion" align="right" sx={summaryHeaderCellSx}>Conversions</TableCell>
                                                                <TableCell data-report-metric="conversion" align="right" sx={summaryHeaderCellSx}>Conv Rate</TableCell>
                                                                <TableCell align="right" sx={summaryHeaderCellSx}>Revenue</TableCell>
                                                                <TableCell align="right" sx={summaryHeaderCellSx}>Avg Value</TableCell>
                                                                {showCost && (
                                                                    <>
                                                                        <TableCell align="right" sx={summaryHeaderCellSx}>Cost</TableCell>
                                                                        <TableCell align="right" sx={summaryHeaderCellSx}>ROAS</TableCell>
                                                                    </>
                                                                )}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                        {platformSummary.map((row, index) => (
                                                            <TableRow
                                                                key={`${row.name}-${index}`}
                                                                hover
                                                                sx={{
                                                                    '&:nth-of-type(odd)': {
                                                                        bgcolor: theme.palette.background.default
                                                                    },
                                                                    '& .MuiTableCell-root': {
                                                                        borderBottom: `1px solid ${theme.palette.divider}`
                                                                    }
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Box sx={{ display: 'grid', gap: 0.25 }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={row.name}>
                                                                            {row.name}
                                                                        </Typography>
                                                                        {row.url ? (
                                                                            <Link href={row.url} target="_blank" rel="noopener noreferrer" underline="hover" variant="caption">
                                                                                View platform
                                                                            </Link>
                                                                        ) : null}
                                                                        {row.error ? (
                                                                            <Tooltip title={row.error}>
                                                                                <Typography component="span" variant="caption" color="error.main" sx={{ width: 'fit-content' }}>
                                                                                    Data unavailable
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        ) : null}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="right">{formatNumber(row.impressions)}</TableCell>
                                                                <TableCell data-report-metric="click" align="right">{formatNumber(row.clicks)}</TableCell>
                                                                <TableCell data-report-metric="click" align="right">{row.ctr.toFixed(2)}%</TableCell>
                                                                <TableCell data-report-metric="conversion" align="right">{formatNumber(row.conversions)}</TableCell>
                                                                <TableCell data-report-metric="conversion" align="right">
                                                                    <Tooltip
                                                                        title={
                                                                            <Box>
                                                                                <Typography fontWeight={900}>Conversion Rate</Typography>
                                                                                <Typography>{'Conversions divided by clicks'}</Typography>
                                                                            </Box>
                                                                        }
                                                                        >
                                                                            <span>{row.convRate.toFixed(2)}%</span>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell align="right">{row.revenueAvailable ? formatMoney(row.revenue) : 'Not available'}</TableCell>
                                                                    <TableCell align="right">{formatMoney(row.avgValue)}</TableCell>
                                                                    {showCost && (
                                                                        <>
                                                                            <TableCell align="right">{formatMoney(row.cost)}</TableCell>
                                                                            <TableCell align="right">{row.revenueAvailable ? `${row.roas.toFixed(2)}x` : 'Not available'}</TableCell>
                                                                        </>
                                                                    )}
                                                                </TableRow>
                                                            ))}
                                                        <TableRow data-report-total="true"
                                                            sx={{
                                                                '& .MuiTableCell-root': {
                                                                    fontWeight: 800,
                                                                    borderBottom: 'none',
                                                                    bgcolor: theme.palette.background.default
                                                                }
                                                            }}
                                                        >
                                                            <TableCell>Totals</TableCell>
                                                            <TableCell align="right">{formatNumber(totals.impressions)}</TableCell>
                                                            <TableCell data-report-metric="click" align="right">{formatNumber(totals.clicks)}</TableCell>
                                                            <TableCell data-report-metric="click" align="right">{totals.ctr.toFixed(2)}%</TableCell>
                                                            <TableCell data-report-metric="conversion" align="right">{formatNumber(totals.conversions)}</TableCell>
                                                            <TableCell data-report-metric="conversion" align="right">{totals.convRate.toFixed(2)}%</TableCell>
                                                            <TableCell align="right">{formatMoney(totals.revenue)}</TableCell>
                                                            <TableCell align="right">{formatMoney(totals.avgValue)}</TableCell>
                                                            {showCost && (
                                                                <>
                                                                    <TableCell align="right">{formatMoney(totals.cost)}</TableCell>
                                                                    <TableCell align="right">{totals.roas.toFixed(2)}x</TableCell>
                                                                </>
                                                            )}
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <Box sx={{ display: 'grid', gap: 2 }}>
                                        {platformSummary.map((row, index) => (
                                            <Card key={`${row.name}-${index}`} variant="outlined" sx={surfaceCardSx}>
                                                <CardContent sx={{ display: 'grid', gap: 1.1 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap title={row.name}>
                                                                {row.name}
                                                            </Typography>
                                                            {row.url ? (
                                                                <Link href={row.url} target="_blank" rel="noopener noreferrer" underline="hover" variant="caption">
                                                                    View platform
                                                                </Link>
                                                            ) : null}
                                                            {row.error ? (
                                                                <Tooltip title={row.error}>
                                                                    <Typography component="span" variant="caption" color="error.main" sx={{ display: 'block', width: 'fit-content' }}>
                                                                        Data unavailable
                                                                    </Typography>
                                                                </Tooltip>
                                                            ) : null}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                px: 1,
                                                                py: 0.45,
                                                                borderRadius: 999,
                                                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                                                            }}
                                                        >
                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                                                CTR {row.ctr.toFixed(2)}%
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                            <Typography variant="caption" color="text.secondary">Impressions</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(row.impressions)}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                            <Typography variant="caption" color="text.secondary">Clicks</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(row.clicks)}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                            <Typography variant="caption" color="text.secondary">Conversions</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatNumber(row.conversions)}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                            <Typography variant="caption" color="text.secondary">Conv Rate</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.convRate.toFixed(2)}%</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gap: 0.75,
                                                            gridTemplateColumns: showCost ? 'repeat(2, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
                                                            pt: 0.25,
                                                            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                            <Typography variant="caption" color="text.secondary">Revenue</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.revenueAvailable ? formatMoney(row.revenue) : 'Not available'}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                            <Typography variant="caption" color="text.secondary">Avg Value</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatMoney(row.avgValue)}</Typography>
                                                        </Box>
                                                    </Box>
                                                    {showCost && (
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gap: 0.75,
                                                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                                                pt: 0.25,
                                                                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                                <Typography variant="caption" color="text.secondary">Cost</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatMoney(row.cost)}</Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'grid', gap: 0.2 }}>
                                                                <Typography variant="caption" color="text.secondary">ROAS</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.revenueAvailable ? `${row.roas.toFixed(2)}x` : 'Not available'}</Typography>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    ''
                )}
            </Box>
            <ReportQuestionAssistant
                reportType="overview"
                reportTitle="Client Overview"
                profile={props.reportProfile}
                contextPayload={{ activeView: 'Overview' }}
            />
        </ThemeProvider>
    );
};

export default Overview;
